/**
 * Zweck einer REVA-ID (#19481) — Spiegel des Backend-Enums
 * `de.immobrain.reva.persistence.document.RevaValuationPurpose`.
 *
 * Der Zweck ist orthogonal zum Lebenszyklus: er sagt, *wofür* die REVA-ID da ist (Research oder Bewertung), der
 * Lebenszyklus, *wie weit* sie ist. Bestandsdokumente ohne Feld gelten als {@link RevaValuationPurpose.VALUATION} —
 * deshalb immer über {@link effectivePurpose} lesen, nie das rohe Feld vergleichen.
 *
 * Policy (MK #19481-18): Aufrufstellen fragen nur die Fähigkeiten aus {@link purposePolicy} ab, nie einen konkreten
 * Zweck. So bleibt die Bedeutung eines Zwecks an genau einer Stelle, und ein weiterer Zweck braucht nur einen Eintrag.
 */
export const RevaValuationPurpose = {
    RESEARCH: "RESEARCH",
    VALUATION: "VALUATION",
} as const;

// Value and type intentionally share the name (a valid TS value/type merge);
// the lint rule doesn't model this and would flag it as a redeclaration.
export type RevaValuationPurpose = (typeof RevaValuationPurpose)[keyof typeof RevaValuationPurpose];

/** Alle Zwecke in der Reihenfolge, in der die GUI sie anbietet. */
export const VALUATION_PURPOSES: readonly RevaValuationPurpose[] = [
    RevaValuationPurpose.RESEARCH,
    RevaValuationPurpose.VALUATION,
];

/** Fähigkeiten eines Zwecks — dieselbe grobe Einteilung wie im Backend. */
export interface PurposePolicy {
    /** (Vorläufiges) Finalisieren, Korrektur, Snapshots und Gutachten-PDFs. */
    finalizationAllowed: boolean;
    /** Zuweisen/Weitergeben und die Value-Order-Aktionen (Reiter „Auftrag"). */
    transferAllowed: boolean;
    /** Wird nach einer Frist ohne Nutzung automatisch gelöscht (Zeitpunkt: `metadata.retentionExpiresAt`). */
    hasRetention: boolean;
    /** Einzig möglicher Zielzweck eines Zweckwechsels, `null` = kein Zweckwechsel. */
    purposeChangeTarget: RevaValuationPurpose | null;
    /** Seite, auf der eine geöffnete bzw. neu angelegte REVA-ID startet: Lage (POIs) oder Objekt-Übersicht. */
    entryPage: "LOCATION" | "OBJECT";
    /**
     * Der Owner darf die REVA-ID auch ohne `reva-lifecycle-deletion` selbst löschen — Spiegel von
     * `RevaValuationPurpose.ownerMayDelete()` bzw. `LifeCycleGuards.checkDeleteAllowed` im Backend.
     */
    ownerMayDelete: boolean;
    /** Würdigungstexte bearbeiten und die Prüfseite „Würdigungen" (`/audit/appreciations`). */
    appreciationsAllowed: boolean;
    /**
     * Der Prüfbereich `/audit/*` (Navigationseintrag „Prüfung" mit Dokumenten, Würdigungen, Änderungshistorie). Ohne
     * ihn laden Research-Abfragen Dokumente über die Reiter Grundbuch und Energieausweis hoch.
     */
    auditAllowed: boolean;
}

const POLICIES: Record<RevaValuationPurpose, PurposePolicy> = {
    RESEARCH: {
        finalizationAllowed: false,
        transferAllowed: false,
        hasRetention: true,
        purposeChangeTarget: RevaValuationPurpose.VALUATION,
        entryPage: "LOCATION",
        ownerMayDelete: true,
        appreciationsAllowed: false,
        auditAllowed: false,
    },
    VALUATION: {
        finalizationAllowed: true,
        transferAllowed: true,
        hasRetention: false,
        purposeChangeTarget: null,
        entryPage: "OBJECT",
        ownerMayDelete: false,
        appreciationsAllowed: true,
        auditAllowed: true,
    },
};

/**
 * Zweck einer REVA-ID mit dem Bestands-Default: fehlt das Feld (Altdokument, älteres Backend), ist es eine Bewertung.
 * Ein unbekannter Wert (neuerer Zweck eines neueren Backends) wird ebenfalls als Bewertung gelesen.
 * @param purpose Rohwert aus `metadata.purpose` bzw. einem Suchtreffer
 */
export function effectivePurpose(purpose: string | null | undefined): RevaValuationPurpose {
    return purpose === RevaValuationPurpose.RESEARCH ? RevaValuationPurpose.RESEARCH : RevaValuationPurpose.VALUATION;
}

/**
 * Fähigkeiten eines Zwecks.
 * @param purpose Rohwert oder Zweck; fehlend = Bewertung (siehe {@link effectivePurpose})
 */
export function purposePolicy(purpose: string | null | undefined): PurposePolicy {
    return POLICIES[effectivePurpose(purpose)];
}
